import { RemediationApiCommandRemediationRequest } from '@ariksa/compliance-policies';
import {
  RemediationApiAggregateRemediationRequest,
  RemediationApiCheckAggregateCommandRemediationRequest,
  RemediationCheck,
  RulesApiGetAllComplianceStandardRequest,
  RulesApiGetDocumentsForCheckRequest,
} from '@ariksa/compliance-policies/api';
import {
  ContextApiGetRiskContextResRequest,
  InsightV2ApiInsightV2Request,
  InsightV2Pagination,
  RelationshipApiGetConnectedEntitiesRequest,
  RiskContextResponse,
} from '@ariksa/inventory-core/api';
import {
  AggregatedAlertsPaginatedResponse,
  AggregatedAlertsSummaryResponse,
  AlertHistoryResponse,
  AlertLogsApiGetLogsForIdsRequest,
  AlertLogsResponse,
  AlertPaginatedResponse,
  AlertsApiGetAggregatedAlertsRequest,
  AlertsApiGetAggregatedAlertsSummaryRequest,
  AlertsApiGetAlertHistoryRequest,
  AlertsApiGetAlertsRequest,
  AlertsApiGetEntitySeverityListRequest,
  AlertsApiGetNotificationStatusRequest,
  AlertsApiGetTopIssuesRequest,
  EntitySeverityListResponse,
  NotificationWorkflowStatus,
  TopIssuesResponse,
} from '@ariksa/notification/api';
import {
  ChartsApiGetDashboardSummaryRequest,
  DashboardCount,
} from '@ariksa/reporting';
import dayjs from 'dayjs';
import { identity } from 'lodash';
import { call } from 'redux-saga/effects';
import { QueryContext } from 'services/utils/QueryContext';
import { takeLatestAction } from 'services/utils/takeLatestAction';
import { Dict } from 'types/utils';

import {
  AlertVaultAlertService,
  isAlertVaultEnabled,
} from 'api/alert-vault/client';
import {
  Api,
  ComplianceService,
  IGetAlertSummary,
  InventoryService,
  NotificationService,
  ReportingService,
} from 'api/index';

import request from '../../../../utils/request';

import { actions } from './slice';

export function* findingsOverviewSaga() {
  // Alert logs were dispatched by the alert drawer but never handled.
  yield takeLatestAction(actions.getAlertLogs, doGetAlertLogs);
  yield takeLatestAction(actions.loadResourceCount, doGetResourceCount);
  yield takeLatestAction(
    actions.loadAlertHistoryByTrend,
    doGetAlertHistoryByTrend,
  );
  yield takeLatestAction(
    actions.loadAlertHistoryByStatus,
    doGetAlertHistoryByStatus,
  );
  yield takeLatestAction(actions.getTopIssuesResolved, doGetTopIssuesResolved);
  yield takeLatestAction(
    actions.getAggregatedAlertRemediationAvailability,
    doGetAggregatedAlertRemediationAvailability,
  );
  yield takeLatestAction(
    actions.getAlertByRuleIdRemediationAvailability,
    doGetAlertByRuleIdRemediationAvailability,
  );

  yield takeLatestAction(actions.loadAlertCategories, doGetAlertCategories);
  yield takeLatestAction(actions.loadAlertsSummary, doGetAlertsSummary);
  yield takeLatestAction(actions.getFindings, doGetAlerts);
  yield takeLatestAction(
    actions.loadResourceFailedChecks,
    doGetResourceFailedChecks,
  );
  yield takeLatestAction(
    actions.loadAlertDetailsSeverity,
    doLoadAlertDetailsSeverity,
  );
  yield takeLatestAction(
    actions.getRemediationCommands,
    doGetRemediationCommands,
  );
  yield takeLatestAction(actions.getAlertsByRuleId, doGetAlertsByRuleId);
  yield takeLatestAction(
    actions.getRemediationByAlertRuleId,
    doGetRemediationByAlertRuleId,
  );

  yield takeLatestAction(actions.getResourceVPC, doGetResourceVPC);
  yield takeLatestAction(
    actions.getFindingDescription,
    doGetFindingDescription,
  );
  yield takeLatestAction(actions.executeRemediation, doExecuteRemediation);
  yield takeLatestAction(
    actions.getAlertWorkflowStatus,
    doGetAlertWorkflowStatus,
  );
  yield takeLatestAction(actions.getConnectedEntities, doGetConnectedEntities);
  yield takeLatestAction(actions.getRiskContext, doGetRiskContext);
  yield takeLatestAction(actions.getCompliance, doGetCompliance);
  yield takeLatestAction(
    actions.getComplianceForFinding,
    doGetAggregatedAlertCompliance,
  );
}

function* doGetAggregatedAlertRemediationAvailability(
  ctx: QueryContext<
    RemediationCheck,
    RemediationApiCheckAggregateCommandRemediationRequest
  >,
) {
  yield call(ctx.fetch, () =>
    ComplianceService.Remediation.checkAggregateCommandRemediation(ctx.params),
  );
}

function* doGetAlertByRuleIdRemediationAvailability(
  ctx: QueryContext<
    RemediationCheck,
    RemediationApiCheckAggregateCommandRemediationRequest
  >,
) {
  yield call(ctx.fetch, () =>
    ComplianceService.Remediation.checkAggregateCommandRemediation(ctx.params),
  );
}

function* doGetTopIssuesResolved(
  ctx: QueryContext<TopIssuesResponse, AlertsApiGetTopIssuesRequest>,
) {
  yield call(ctx.fetch, () =>
    isAlertVaultEnabled()
      ? AlertVaultAlertService.topIssues(ctx.params)
      : NotificationService.Alerts.getTopIssues(ctx.params),
  );
}

export function* doGetResourceCount(
  ctx: QueryContext<DashboardCount, ChartsApiGetDashboardSummaryRequest>,
) {
  yield call(
    ctx.fetch,
    () => ReportingService.Charts.getDashboardSummary(ctx.params),
    {
      errorMsg: 'Failed to get resource count!',
      cacheKey: 'findingsResourceCount',
    },
  );
}

export function* doGetAlertHistoryByTrend(
  ctx: QueryContext<AlertHistoryResponse, AlertsApiGetAlertHistoryRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultAlertService.history(ctx.params)
        : NotificationService.Alerts.getAlertHistory(ctx.params),
    {
      errorMsg: 'Failed to get alert history by trend!',
      cacheKey: 'alertHistoryByTrend',
    },
  );
}

export function* doGetAlertHistoryByStatus(
  ctx: QueryContext<AlertHistoryResponse, AlertsApiGetAlertHistoryRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultAlertService.history(ctx.params)
        : NotificationService.Alerts.getAlertHistory(ctx.params),
    {
      errorMsg: 'Failed to get alert history by status!',
      cacheKey: 'alertHistoryByStatus',
    },
  );
}

function* doGetConnectedEntities(
  ctx: QueryContext<
    InsightV2Pagination,
    RelationshipApiGetConnectedEntitiesRequest
  >,
) {
  yield call(ctx.fetch, async () =>
    InventoryService.Relationship.getConnectedEntities(ctx.params),
  );
}

function* doExecuteRemediation(
  ctx: QueryContext<any, RemediationApiCommandRemediationRequest>,
) {
  yield call(
    ctx.fetch,
    async () => ComplianceService.Remediation.commandRemediation(ctx.params),
    { successMsg: 'Remediation executed successfully' },
  );
}

function* doGetResourceVPC(
  ctx: QueryContext<InsightV2Pagination, InsightV2ApiInsightV2Request>,
) {
  yield call(ctx.fetch, async () =>
    InventoryService.InsightV2.insightV2(ctx.params),
  );
}

function* doGetFindingDescription(
  ctx: QueryContext<any, RulesApiGetDocumentsForCheckRequest>,
) {
  yield call(ctx.fetch, async () =>
    ComplianceService.Rules.getDocumentsForCheck(ctx.params),
  );
}

function* doGetRemediationByAlertRuleId(
  ctx: QueryContext<any, RemediationApiAggregateRemediationRequest>,
) {
  yield call(ctx.fetch, () =>
    ComplianceService.Remediation.aggregateRemediation(ctx.params),
  );
}

function* doGetAlertsByRuleId(
  ctx: QueryContext<AlertPaginatedResponse, AlertsApiGetAlertsRequest>,
) {
  yield call(ctx.fetch, () => {
    if (!isAlertVaultEnabled()) {
      return NotificationService.Alerts.getAlerts({
        ...ctx.params,
        pageNumber: ctx.page.page_number,
        pageSize: ctx.page.page_size,
      });
    }

    const params = ctx.params as AlertsApiGetAlertsRequest & {
      groupId?: string;
    };
    const [groupBy, groupKey] = String(params.groupId ?? '').split(':', 2);
    const common = {
      accountId: params.accountId,
      environmentId: params.environmentId,
      category: params.alertCategory,
      resourceType: params.resourceTypes?.[0],
      severity: params.severities,
      search: params.searchString,
      from: params.startDate,
      to: params.endDate,
      status: params.status,
      pageNumber: ctx.page.page_number,
      pageSize: ctx.page.page_size,
    };

    // Overview aggregates are currently resource-grouped. Keep the drawer
    // scoped to the selected aggregate instead of falling back to every alert
    // in the category. Policy-grouped aggregates use their policy key.
    if (groupBy === 'resource' && groupKey) {
      return AlertVaultAlertService.resourceAlerts(groupKey, common);
    }
    // Finding rows group one check across policies; list by its group.
    if (groupBy === 'finding' && groupKey) {
      return AlertVaultAlertService.list({ ...common, groupId: groupKey });
    }

    return AlertVaultAlertService.list({
      ...common,
      policyId: groupBy === 'policy' ? groupKey : params.alertRuleId?.[0],
    });
  });
}

function* doGetRemediationCommands(
  ctx: QueryContext<any, RemediationApiCommandRemediationRequest>,
) {
  yield call(ctx.fetch, () =>
    ComplianceService.Remediation.commandRemediation(ctx.params),
  );
}

export function* doGetAlertCategories(
  ctx: QueryContext<
    AggregatedAlertsSummaryResponse,
    AlertsApiGetAggregatedAlertsSummaryRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultAlertService.categories(ctx.params)
        : NotificationService.Alerts.getAggregatedAlertsSummary(ctx.params),
    {
      errorMsg: 'Failed to get alert categories!',
      cacheKey: 'findingsAlertCategories',
    },
  );
}

export function* doGetAlertsSummary(ctx: QueryContext<any>) {
  const { accountId, environmentId, alertCategory } = ctx.params;

  const payload: IGetAlertSummary = {
    environment_id: environmentId,
    account_id: accountId,
    category: alertCategory,
    start_date: dayjs().utc().subtract(1, 'day').format('YYYY-MM-DD'),
    end_date: dayjs().utc().format('YYYY-MM-DD'),
  };

  yield call(
    ctx.fetch,
    () =>
      isAlertVaultEnabled()
        ? AlertVaultAlertService.summary({
            accountId,
            environmentId,
            category: alertCategory,
          })
        : request(Api.getAlertsSummary(payload)),
    { map: identity },
  );
}

export function* doGetAlerts(
  ctx: QueryContext<
    AggregatedAlertsPaginatedResponse,
    AlertsApiGetAggregatedAlertsRequest
  >,
) {
  yield call(ctx.fetch, () =>
    isAlertVaultEnabled()
      ? AlertVaultAlertService.aggregates({
          accountId: ctx.params.accountId,
          environmentId: ctx.params.environmentId,
          category: ctx.params.alertCategory,
          categoryClass: ctx.params.categoryClass,
          resourceType: ctx.params.resourceTypes?.[0],
          severity: ctx.params.severities,
          search: ctx.params.searchString,
          from: ctx.params.startDate,
          to: ctx.params.endDate,
          // Group by PolicyHub finding (one check across resource types and
          // policies) to match the legacy findings view.
          groupBy: 'finding',
          pageNumber: ctx.page.page_number,
          pageSize: ctx.page.page_size,
        })
      : NotificationService.Alerts.getAggregatedAlerts(ctx.params),
  );
}

export function* doGetResourceFailedChecks(
  ctx: QueryContext<AlertPaginatedResponse, AlertsApiGetAlertsRequest>,
) {
  yield call(ctx.fetch, () =>
    isAlertVaultEnabled()
      ? AlertVaultAlertService.list({
          accountId: ctx.params.accountId,
          environmentId: ctx.params.environmentId,
          resourceType: ctx.params.resourceTypes?.[0],
          category: ctx.params.alertCategory,
          categoryClass: ctx.params.categoryClass,
          severity: ctx.params.severities,
          pageNumber: ctx.page.page_number,
          pageSize: ctx.page.page_size,
        })
      : NotificationService.Alerts.getAlerts(ctx.params),
  );
}

export function* doLoadAlertDetailsSeverity(
  ctx: QueryContext<
    EntitySeverityListResponse,
    AlertsApiGetEntitySeverityListRequest
  >,
) {
  yield call(ctx.fetch, () =>
    NotificationService.Alerts.getEntitySeverityList(ctx.params),
  );
}

export function* doGetAlertWorkflowStatus(
  ctx: QueryContext<
    NotificationWorkflowStatus[],
    AlertsApiGetNotificationStatusRequest
  >,
) {
  yield call(ctx.fetch, () =>
    isAlertVaultEnabled()
      ? AlertVaultAlertService.deliveryStatus({
          alertId: ctx.params.alertId?.[0],
        })
      : NotificationService.Alerts.getNotificationStatus(ctx.params),
  );
}

export function* doGetAlertLogs(
  ctx: QueryContext<AlertLogsResponse[], AlertLogsApiGetLogsForIdsRequest>,
) {
  yield call(ctx.fetch, () =>
    isAlertVaultEnabled() && ctx.params.alertId?.[0]
      ? AlertVaultAlertService.activity(ctx.params.alertId[0])
      : NotificationService.AlertsLogs.getLogsForIds(ctx.params),
  );
}

export function* doGetRiskContext(
  ctx: QueryContext<
    Dict<RiskContextResponse[]>,
    ContextApiGetRiskContextResRequest
  >,
) {
  yield call(ctx.fetch, () =>
    InventoryService.Context.getRiskContextRes(ctx.params),
  );
}

export function* doGetCompliance(
  ctx: QueryContext<Dict<string[]>, RulesApiGetAllComplianceStandardRequest>,
) {
  yield call(ctx.fetch, () =>
    ComplianceService.Rules.getAllComplianceStandard(ctx.params),
  );
}

export function* doGetAggregatedAlertCompliance(
  ctx: QueryContext<Dict<string[]>, RulesApiGetAllComplianceStandardRequest>,
) {
  yield call(ctx.fetch, () =>
    ComplianceService.Rules.getAllComplianceStandard(ctx.params),
  );
}
